import { Component, EventEmitter, Input, OnChanges, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { tabsAnimations } from '../../../../util/animations/tabs-animations';
import { isEqual } from 'lodash';

@Component({
  selector: 'app-tabs-with-counter',
  templateUrl: './tabs-with-counter.component.html',
  styleUrls: ['./tabs-with-counter.component.scss', '../tabs/tabs.component.scss'],
  animations: [tabsAnimations],
})

/**
 * Can be used with map [{'vodka': 10}, {'pivo': 2}, {'chebureki': 8}]
 * As well with regular array ['vodka', 'pivo', 'chebureki']
 *
 * In first case the counter will be added automatically near the name
 * In second case the counter will be hidden
 */
export class TabsWithCounterComponent implements OnChanges {

  @Input() tabNamesRaw: Map<any, any> | string[] = new Map<any, any>([]);
  @Input() initialTabIndex: number; // tab index, will be selected initially
  @Input() type = TabType.Main; // border rounded with main-8 or grey, table-tab or underlined styling
  @Input() additionalClasses = ''; // Example: 'centered-column-container column-gap-1'
  @Input() showBottomBorder = false;
  @Output() selectedTabEmitter: EventEmitter<string> = new EventEmitter<string>();

  tabNames: TabWithCounterButton[] = [];
  tabStyleList: TabStyle = ['modal-button', 'tab-size'];

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.tabNamesRaw && this.hasChanges(changes.tabNamesRaw))
      || (changes.initialTabIndex && this.hasChanges(changes.initialTabIndex))) {
      this.initializeTabNames();
    }

    if (changes.type) {
      this.updateTabStyles();
    }

    if (changes.initialTabIndex && this.hasChanges(changes.initialTabIndex)) {
      this.changeTab(this.initialTabIndex);
    }
  }

  changeTab(index: number): void {
    this.tabNames.forEach((tab) => tab.currentClass = '');
    const preparedIndex = index ? index : 0;
    this.tabNames[preparedIndex].currentClass = 'main-button';
    this.selectedTabEmitter.emit(this.tabNames[preparedIndex].name);
  }

  private initializeTabNames(): void {
    if (Array.isArray(this.tabNamesRaw) && this.tabNamesRaw.every((item) => typeof item === 'string')) {
      this.tabNames = this.tabNamesRaw.map((tabName) => new TabWithCounterButton(tabName, null, '', '', false));
    } else {
      this.tabNames = Array.from(this.tabNamesRaw.values()).map((tab: TabWithCounterButton) => new TabWithCounterButton(tab.name, tab.count, '', tab.iconNameAndStyling, tab.isDisabled));
    }

    if (this.tabNames.length > 0) {
      this.tabNames[this.initialTabIndex ?? 0].currentClass = 'main-button'; // Set for the selected tab
    }
  }

  private updateTabStyles(): void {
    switch (this.type) {
      case TabType.Table:
        this.tabStyleList = ['table-tab', 'aligned-row-container'];
        break;
      case TabType.Grey:
        this.tabStyleList = ['grey-tab', 'centered-row-container'];
        break;
      case TabType.Underlined:
        this.tabStyleList = ['underlined-tab', 'centered-row-container'];
        break;
      case TabType.Main:
        this.tabStyleList = ['modal-button', 'tab-size'];
        break;
      case TabType.MainUnderlined:
        this.tabStyleList = ['underlined-tab-main', 'centered-row-container'];
        break;
      default:
        this.tabStyleList = ['modal-button', 'tab-size'];
        break;
    }
  }

  private hasChanges(obj: SimpleChange): boolean {
    return !isEqual(obj?.previousValue, obj?.currentValue);
  }
}

export class TabWithCounterButton {
  constructor(public name: string, public count: number, public currentClass: string, public iconNameAndStyling: string, public isDisabled: boolean) {
    this.name = name;
    this.count = count;
    this.currentClass = currentClass;
    this.iconNameAndStyling = iconNameAndStyling;
    this.isDisabled = isDisabled;
  }
}


export enum TabType {
  Main = 'main', // rounded main-8 color
  Table = 'table', // rounded on top with grey color
  Grey = 'grey', // rounded with grey color
  Underlined = 'underlined', // underlined
  MainUnderlined = 'main-underlined',
}

type TabStyle =
  ['modal-button', 'tab-size'] | // main
  ['table-tab', 'aligned-row-container'] | // table
  ['grey-tab', 'centered-row-container'] | // grey
  ['underlined-tab', 'centered-row-container'] | // underlined
  ['underlined-tab-main', 'centered-row-container']; // underlined style with main color
